
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Calendar, User, CheckSquare, Clock, Tag } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { parseChecklists, calculateChecklistStats } from '@/utils/checklistUtils';
import { CircularProgress, ProgressState } from '@/components/kanban/CircularProgress';
import type { Tables } from '@/integrations/supabase/types';
import { useOptimizedCardPresenceContext } from '@/contexts/OptimizedCardPresenceContext';
import { useEffect } from 'react';

type CardType = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

interface CalendarCardDialogProps {
  card: CardType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CalendarCardDialog = ({ card, open, onOpenChange }: CalendarCardDialogProps) => {
  const { getTagName, getTagColor, updateCard } = useBoardContext();

  // Parse checklists and calculate stats
  const checklists = parseChecklists(card);
  const checklistStats = calculateChecklistStats(checklists);
  const hasChecklists = checklistStats.totalChecklists > 0;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = () => {
    const startDate = formatDate(card.start_date);
    const endDate = formatDate(card.end_date);
    
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    } else if (startDate) {
      return `Start: ${startDate}`;
    } else if (endDate) {
      return `Due: ${endDate}`;
    }
    return null;
  };

  // Handle progress state change
  const handleProgressChange = (newState: ProgressState) => {
    updateCard({
      cardId: card.id,
      updates: {
        progress_state: newState,
      },
    });
  };

  const currentProgressState = (card as any).progress_state || 'not_started';

  // **OPTIMIZED: Use only the optimized presence context (optional)**
  let setCardStatus: ((cardId: string, status: "viewing" | "editing") => void) | undefined;
  try {
    const context = useOptimizedCardPresenceContext();
    setCardStatus = context.setCardStatus;
  } catch {
    // Context not available - presence tracking not needed in this case
    setCardStatus = undefined;
  }

  useEffect(() => {
    if (open && setCardStatus) {
      setCardStatus(card.id, "viewing");
    }
    
    return () => {
      if (open && setCardStatus) {
        setCardStatus(card.id, "viewing");
      }
    };
  }, [open, setCardStatus, card.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex-1">{card.title}</span>
            <CircularProgress
              state={currentProgressState as ProgressState}
              onStateChange={handleProgressChange}
              size="md"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Tags */}
          {card.tags && card.tags.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Tag className="h-4 w-4" />
                Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {card.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-sm"
                    style={{
                      backgroundColor: getTagColor(tag),
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {getTagName(tag)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {card.description && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Description</div>
              <p className="text-sm text-foreground whitespace-pre-wrap">{card.description}</p>
            </div>
          )}

          {/* Dates */}
          {(card.start_date || card.end_date) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Schedule
              </div>
              <div className="text-sm text-foreground">
                {formatDateRange()}
              </div>
            </div>
          )}

          {/* Category */}
          {card.category && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Category</div>
              <Badge variant="outline" className="text-sm">
                {card.category}
              </Badge>
            </div>
          )}

          {/* Assigned User */}
          {card.assigned_profile && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="h-4 w-4" />
                Assigned To
              </div>
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-sm">
                    {card.assigned_profile.full_name?.charAt(0) || 
                     card.assigned_profile.username?.charAt(0) || 
                     '?'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-foreground">
                  {card.assigned_profile.full_name || card.assigned_profile.username}
                </span>
              </div>
            </div>
          )}

          {/* Checklist Progress */}
          {hasChecklists && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CheckSquare className="h-4 w-4" />
                Checklist Progress
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>{checklistStats.completedItems} of {checklistStats.totalItems} tasks completed</span>
                  <Badge variant="secondary" className="text-xs">
                    {checklistStats.completionPercentage}%
                  </Badge>
                </div>
                <Progress value={checklistStats.completionPercentage} className="h-2" />
              </div>
              
              {/* Checklist Details */}
              <div className="space-y-3 mt-4">
                {checklists.map((checklist, checklistIndex) => (
                  <div key={checklistIndex} className="space-y-2">
                    <div className="text-sm font-medium">{checklist.name || `Checklist ${checklistIndex + 1}`}</div>
                    <div className="space-y-1 pl-4">
                      {checklist.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center gap-2 text-sm">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            item.completed 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300'
                          }`}>
                            {item.completed && '✓'}
                          </div>
                          <span className={item.completed ? 'line-through text-muted-foreground' : ''}>
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Creation Date */}
          <div className="space-y-2 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4" />
              Created
            </div>
            <div className="text-sm text-muted-foreground">
              {formatDate(card.created_at)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
